/**
 * Toast notifications — a small reactive queue rendered by ToastHost.
 *
 * Usage:
 *   import { toast } from '$domain/toasts.svelte';
 *   toast.success('Saved.');
 *   toast.error('Network error — retry?', { duration: 8000 });
 *
 * Each toast auto-dismisses after `duration` ms (default 3500). Pass
 * `duration: 0` to make it sticky until the user closes it.
 */

export type ToastKind = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /** Auto-dismiss after N ms. 0 = never. */
  duration: number;
  /** Optional inline CTA. When present the toast renders a small
   *  trailing button; clicking it runs `onClick` and dismisses. */
  action?: { label: string; onClick: () => void };
}

let nextId = 0;
let _toasts: Toast[] = $state([]);

export const toasts = {
  get list(): Toast[] {
    return _toasts;
  },
};

export function dismissToast(id: number): void {
  _toasts = _toasts.filter((t) => t.id !== id);
}

function push(
  kind: ToastKind,
  message: string,
  duration: number,
  action?: Toast['action'],
): number {
  const id = ++nextId;
  _toasts = [..._toasts, { id, kind, message, duration, action }];
  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }
  return id;
}

interface ToastOpts {
  duration?: number;
  action?: Toast['action'];
}

export const toast = {
  info(message: string, opts: ToastOpts = {}): number {
    return push('info', message, opts.duration ?? 3500, opts.action);
  },
  success(message: string, opts: ToastOpts = {}): number {
    return push('success', message, opts.duration ?? 3500, opts.action);
  },
  warn(message: string, opts: ToastOpts = {}): number {
    return push('warn', message, opts.duration ?? 5000, opts.action);
  },
  error(message: string, opts: ToastOpts = {}): number {
    return push('error', message, opts.duration ?? 6000, opts.action);
  },
};
